import { scaleLinear } from '@mui/x-charts-vendor/d3-scale';
import isEqual from 'lodash.isequal';
import uniqWith from 'lodash.uniqwith';
import { match } from 'ts-pattern';
import { v1 } from 'uuid';
import {
  ApplicationDataFields,
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
  JobDataFields,
  MetricIdType,
  OfferDataFields,
  SegmentBy,
  ValueTypes,
} from '../types';
import {
  applicationCurrentStageValuesTranslationMap,
  applicationStatusValuesTranslationMap,
  definedManager2TranslationKeyMap,
  employmentRegularityTranslationKeyMap,
  genderTranslationKeyMap,
  hireStatusValuesTranslationMap,
  jobStatusValuesTranslationMap,
  managerOrICTranslationKeyMap,
  maritalStatusTranslationKeyMap,
  regionalLocalTranslationKeyMap,
  regretNonRegretTranslationKeyMap,
  voluntaryNonVoluntaryTranslationKeyMap,
} from './translations';
import {
  ApplicationCurrentStateValues,
  ApplicationStatusValues,
  DefinedManager2Values,
  EmploymentRegularityValues,
  GenderValues,
  HireStatusValues,
  JobStatusValues,
  ManagerOrIcValues,
  MartialStatusValues,
  RegionalOrLocalValues,
  RegretNonRegretValues,
  SortTypes,
  VoluntaryInvoluntaryValues,
} from './types';

export const uuid = () => v1();

export const mergeSegmentations = (
  s1: SegmentBy[] | undefined,
  s2: SegmentBy[] | undefined,
  metrics: MetricIdType[],
  maxSegmentationLevel: number
): SegmentBy[] | undefined => {
  if (metrics.length > 1) {
    return s1 && s1.length > 0 ? s1 : undefined;
  }
  const ss: SegmentBy[] = [...(s1 ?? []), ...(s2 ?? [])];
  if (ss.length > 0 && ss.length <= maxSegmentationLevel) {
    return ss;
  } else {
    return s1 && s1.length > 0 ? s1 : undefined;
  }
};

export const shortenLabel = (label: string, maxChars = 10): string => {
  const shortenedSegment = label.substring(0, maxChars);
  return label.length > maxChars ? shortenedSegment : label;
};

// TODO: could use a generic type for label so that we don't have to cast
// but that means using GenderValues, ManagerOrIcValues, etc when parsing the backend response
export const translateLabel = (
  label: ValueTypes,
  dataField: DataFieldWithDataType
): string | null => {
  return match(dataField)
    .with(
      { dataType: DataTypes.EMPLOYEE, dataField: EmployeeDataFields.GENDER },
      () => genderTranslationKeyMap[label as GenderValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.MANAGER_OR_IC,
      },
      () => managerOrICTranslationKeyMap[label as ManagerOrIcValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.DEFINED_MANAGER,
      },
      () => managerOrICTranslationKeyMap[label as ManagerOrIcValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.DEFINED_MANAGER_2,
      },
      () => definedManager2TranslationKeyMap[label as DefinedManager2Values]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.MARITAL_STATUS,
      },
      () => maritalStatusTranslationKeyMap[label as MartialStatusValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.REGIONAL_OR_LOCAL,
      },
      () => regionalLocalTranslationKeyMap[label as RegionalOrLocalValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.LOCAL_OR_NON_LOCAL,
      },
      () => regionalLocalTranslationKeyMap[label as RegionalOrLocalValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.REGRET_ATTRITION,
      },
      () => regretNonRegretTranslationKeyMap[label as RegretNonRegretValues]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.VOLUNTARY_OR_INVOLUNTARY_ATTRITION,
      },
      () =>
        voluntaryNonVoluntaryTranslationKeyMap[
          label as VoluntaryInvoluntaryValues
        ]
    )
    .with(
      {
        dataType: DataTypes.EMPLOYEE,
        dataField: EmployeeDataFields.EMPLOYMENT_REGULARITY,
      },
      () =>
        employmentRegularityTranslationKeyMap[
          label as EmploymentRegularityValues
        ]
    )
    .with(
      {
        dataType: DataTypes.APPLICATION,
        dataField: ApplicationDataFields.STATUS,
      },
      () =>
        applicationStatusValuesTranslationMap[label as ApplicationStatusValues]
    )
    .with(
      {
        dataType: DataTypes.APPLICATION,
        dataField: ApplicationDataFields.STANDARDIZED_CURRENT_STAGE,
      },
      () =>
        applicationCurrentStageValuesTranslationMap[
          label as ApplicationCurrentStateValues
        ]
    )
    .with(
      {
        dataType: DataTypes.APPLICATION,
        dataField: ApplicationDataFields.APPLICATION_CURRENT_STAGE,
      },
      () =>
        applicationCurrentStageValuesTranslationMap[
          label as ApplicationCurrentStateValues
        ]
    )
    .with(
      {
        dataType: DataTypes.APPLICATION,
        dataField: ApplicationDataFields.APPLICATION_CURRENT_STAGE_LEVEL_1,
      },
      () =>
        applicationCurrentStageValuesTranslationMap[
          label as ApplicationCurrentStateValues
        ]
    )
    .with(
      {
        dataType: DataTypes.APPLICATION,
        dataField: ApplicationDataFields.APPLICATION_CURRENT_STAGE_LEVEL_2,
      },
      () =>
        applicationCurrentStageValuesTranslationMap[
          label as ApplicationCurrentStateValues
        ]
    )
    .with(
      {
        dataType: DataTypes.APPLICATION,
        dataField: ApplicationDataFields.APPLICATION_CURRENT_STAGE_LEVEL_3,
      },
      () =>
        applicationCurrentStageValuesTranslationMap[
          label as ApplicationCurrentStateValues
        ]
    )
    .with(
      { dataType: DataTypes.JOB, dataField: JobDataFields.STATUS },
      () => jobStatusValuesTranslationMap[label as JobStatusValues]
    )
    .with(
      { dataType: DataTypes.OFFER, dataField: OfferDataFields.STATUS },
      () => hireStatusValuesTranslationMap[label as HireStatusValues]
    )
    .otherwise(() => null);
};

export const delay = (delayInms: number) => {
  return new Promise((resolve) => setTimeout(resolve, delayInms));
};

export const sortTypeTranslationKeys: Record<SortTypes, string> = {
  [SortTypes.ASC]: 'common:commonValues.sortValues.az',
  [SortTypes.DESC]: 'common:commonValues.sortValues.za',
  [SortTypes.HIGH]: 'common:commonValues.sortValues.highLow',
  [SortTypes.LOW]: 'common:commonValues.sortValues.lowHigh',
} as const;

export const removeDuplicates = <T>(arr: T[]) => uniqWith(arr, isEqual);

export const arrayStartsWith = <T>(array1: T[], array2: T[]) => {
  if (array2.length > array1.length) {
    return false;
  } else {
    return array2.every((item, index) => item === array1[index]);
  }
};

export const getDeltaSign = (delta: number) => {
  // since minus is already shown in the value, we don't need to show it again
  return delta >= 0 ? '+' : '';
};

// Create true object reference-based memoization (single argument only)
export const memoizeByReference = <T extends object, R>(
  fn: (arg: T) => R
): ((arg: T) => R) => {
  const cache = new WeakMap<T, R>();

  return function (this: any, arg: T): R {
    if (cache.has(arg)) {
      return cache.get(arg)!;
    }

    const result = fn.apply(this, [arg]);
    cache.set(arg, result);
    return result;
  };
};

/**
 * Generates unique tick values using D3's scaleLinear ticks method (same as MUI's 'auto' tickInterval).
 * This matches the default behavior of MUI X Charts when tickInterval is set to 'auto'.
 * If a valueFormatter is provided, ticks are filtered to ensure unique formatted values (keeps last occurrence when duplicates exist).
 * Ensures the last tick is >= max value.
 *
 * @param min - The minimum value of the domain
 * @param max - The maximum value of the domain
 * @param valueFormatter - Optional formatter function. If provided, ticks are filtered to ensure unique formatted values (keeps last occurrence when duplicates exist)
 * @param tickNumber - Optional desired number of ticks. Defaults to 5 to match MUI's typical behavior
 * @returns Array of tick values
 *
 * @example
 * generateUniqueTicksFromMinMax(0, 100) // returns [0, 20, 40, 60, 80, 100]
 * generateUniqueTicksFromMinMax(0, 100, (v) => Math.round(v).toString()) // filters to unique formatted values
 * generateUniqueTicksFromMinMax(0, 100, undefined, 10) // returns approximately 10 ticks
 */
export const generateUniqueTicksFromMinMax = (
  min: number,
  max: number,
  valueFormatter?: (value: number) => string,
  tickNumber: number = 5
): number[] => {
  const scale = scaleLinear().domain([min, max]).range([0, 1]);
  let ticks = scale.ticks(tickNumber);
  // Ensure last tick >= max by adding one more tick if needed
  if (ticks.length >= 2 && ticks[ticks.length - 1] < max) {
    const step = ticks[1] - ticks[0];
    ticks = [...ticks, ticks[ticks.length - 1] + step];
  }

  const hasDuplicates =
    valueFormatter &&
    removeDuplicates(ticks.map(valueFormatter)).length !== ticks.length;
  if (!hasDuplicates) {
    return ticks;
  }

  // Determine if formatter is numerical (formatted output can be directly parsed as number)
  const isNumericalFormatter = (): boolean => {
    // Check if all formatted tick values can be directly parsed as numbers
    return ticks.every((tick) => {
      const formatted = String(valueFormatter(tick));
      const parsed = Number(formatted);
      return !isNaN(parsed) && isFinite(parsed) && formatted.trim() !== '';
    });
  };

  if (isNumericalFormatter()) {
    // Numerical formatter approach: keep the tick closest to the numerical formatted value
    // Group ticks by their formatted values
    const formattedValueGroups = new Map<string, number[]>();
    for (const tick of ticks) {
      const formattedValue = valueFormatter(tick);
      if (!formattedValueGroups.has(formattedValue)) {
        formattedValueGroups.set(formattedValue, []);
      }
      formattedValueGroups.get(formattedValue)!.push(tick);
    }

    // For each group, find the tick closest to the parsed numerical value
    const result: number[] = [];
    for (const [formattedValue, tickGroup] of formattedValueGroups) {
      // Parse the formatted value directly to get the target number
      const parsedValue = Number(String(formattedValue));

      if (isNaN(parsedValue) || !isFinite(parsedValue)) {
        // If can't parse, just keep the first tick
        result.push(tickGroup[0]);
      } else {
        // Find the tick whose value is closest to the parsed value
        const closestTick = tickGroup.reduce((closest, current) => {
          const closestDiff = Math.abs(closest - parsedValue);
          const currentDiff = Math.abs(current - parsedValue);
          return currentDiff < closestDiff ? current : closest;
        });
        result.push(closestTick);
      }
    }

    // Sort result to maintain original tick order
    return result.sort((a, b) => a - b);
  } else {
    // Non-numerical formatter approach: use step count
    // Inspired by https://github.com/chartjs/Chart.js/issues/4748#issue-257030298
    // Assumes linear scale, and that values inside the 'ticks' array are equally spaced
    // Optimum step count identifies the minimum distance between values such that their
    // formatted representation is different. For example, if the formatted representation is
    // [0%, 0.1%, 0.1%, 0.1%, 0.2%, 0.2%, 0.2%, 0.3%, 0.3%...], the optimum step count is 3
    // and we show the ticks at index 0, 3, 6, and so on
    const findOptimumStepCount = (): number => {
      for (let i = 0; i < ticks.length - 1; i++) {
        if (
          i !== 0 &&
          valueFormatter(ticks[i]) !== valueFormatter(ticks[i + 1])
        ) {
          return i;
        }
      }
      // If all formatted values are the same, return 1 to show all ticks
      return 1;
    };

    const optimumStepCount = findOptimumStepCount();
    const result: number[] = [];

    // Show ticks at intervals of the optimum step count
    for (let i = 0; i < ticks.length; i++) {
      if (i % optimumStepCount === 0) {
        result.push(ticks[i]);
      }
    }

    return result;
  }
};
